<template>
  <v-card class="question-card">
    <v-card-text class="pa-6">
      <!-- Question Statement -->
      <div class="question-header mb-6">
        <div
          class="text-body-2 font-weight-medium mb-4 text-justify"
          style="text-align: justify !important"
        >
          <MathRenderer :content="question.question_text || question.statement" />
        </div>

        <!-- Question Image (if exists) -->
        <div v-if="question.statement_image_url" class="mt-4 mb-2">
          <AuthenticatedImage
            :src="question.statement_image_url"
            max-height="400"
            image-class="border rounded"
          />
        </div>
      </div>

      <!-- Multiple Choice Options -->
      <div v-if="isMultipleChoice" class="options-container">
        <v-radio-group v-model="selectedOption" :disabled="answered" class="options-group">
          <div
            v-for="(option, index) in questionOptions"
            :key="option.id || index"
            class="option-card mb-3"
            :class="{
              'option-selected': selectedOption === (option.id || index),
              'option-disabled': answered,
            }"
          >
            <label class="option-content">
              <!-- Radio button -->
              <div class="option-radio">
                <v-radio
                  :value="option.id || index"
                  color="primary"
                  :disabled="answered"
                  hide-details
                />
              </div>

              <!-- Option letter -->
              <div class="option-letter">
                {{ option.label || String.fromCharCode(65 + index) }}.
              </div>

              <!-- Option content -->
              <div class="option-body">
                <!-- Text option -->
                <div
                  v-if="(option.text && option.text.trim()) || typeof option === 'string'"
                  class="option-text"
                >
                  <MathRenderer :content="option.text || option" />
                </div>
                <!-- Image option -->
                <div v-if="option.image_url" class="option-image">
                  <AuthenticatedImage
                    :key="`${option.id}-${option.image_url}`"
                    :src="option.image_url"
                    :alt="`Opción ${option.label || String.fromCharCode(65 + index)}`"
                    max-height="120"
                    max-width="200"
                    image-class="rounded"
                  />
                </div>
                <!-- Fallback -->
                <div
                  v-if="!option.text && !option.image_url && typeof option !== 'string'"
                  class="option-text text-disabled"
                >
                  Opción sin contenido
                </div>
              </div>
            </label>
          </div>
        </v-radio-group>
      </div>

      <!-- True/False Options -->
      <div v-else-if="isTrueFalse" class="options-container">
        <v-radio-group v-model="selectedOption" :disabled="answered" class="options-group">
          <v-radio :value="1" label="Verdadero" color="success" class="option-radio mb-3" />
          <v-radio :value="0" label="Falso" color="error" class="option-radio mb-3" />
        </v-radio-group>
      </div>

      <!-- Text/Essay Input -->
      <div v-else-if="isText || isEssay" class="options-container">
        <v-textarea
          v-model="textAnswer"
          :label="isEssay ? 'Escribe tu respuesta (ensayo)' : 'Escribe tu respuesta'"
          :placeholder="
            isEssay ? 'Desarrolla tu respuesta de forma detallada...' : 'Escribe tu respuesta...'
          "
          variant="outlined"
          :rows="isEssay ? 8 : 4"
          :disabled="answered"
          counter
          :rules="textRules"
        />
      </div>

      <!-- Action Buttons -->
      <v-card-actions class="px-0 pt-6">
        <v-spacer />

        <div class="d-flex align-center ga-4">
          <!-- Time indicator -->
          <v-chip
            :color="timeRemaining <= 10 ? 'error' : timeRemaining <= 30 ? 'warning' : 'primary'"
            variant="outlined"
            size="small"
          >
            <v-icon left size="16">mdi-clock</v-icon>
            {{ formatTime(timeRemaining) }}
          </v-chip>

          <!-- Submit Button -->
          <v-btn
            color="primary"
            size="large"
            :disabled="!canSubmit || answered"
            @click="submitAnswer"
            class="px-6"
          >
            <v-icon left>mdi-send</v-icon>
            {{ answered ? 'Respondida' : 'Responder' }}
          </v-btn>
        </div>
      </v-card-actions>

      <!-- Auto-progression notice -->
      <v-alert
        v-if="timeRemaining <= 10"
        type="warning"
        variant="tonal"
        class="mt-4"
        density="compact"
      >
        La pregunta se enviará vacia automáticamente, si no es respondida antes de que se agote el
        tiempo.
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import AuthenticatedImage from '@/components/common/AuthenticatedImage.vue'
import MathRenderer from '@/components/common/MathRenderer.vue'

const props = defineProps({
  question: {
    type: Object,
    required: true,
  },
  timeRemaining: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['answer-selected'])

// Component state
const selectedOption = ref(null)
const textAnswer = ref('')
const answered = ref(false)

// Question type detection
const isMultipleChoice = computed(() => {
  return (
    props.question.question_type === 'multiple_choice' ||
    (props.question.options && props.question.options.length > 0)
  )
})

const isTrueFalse = computed(() => {
  return props.question.question_type === 'true_false'
})

const isText = computed(() => {
  return props.question.question_type === 'text'
})

const isEssay = computed(() => {
  return props.question.question_type === 'essay'
})

// Process question options
const questionOptions = computed(() => {
  if (!props.question.options) return []

  // New normalized array format with objects {id, label, text, image_url, is_correct}
  if (Array.isArray(props.question.options)) {
    return props.question.options.filter((option) => {
      // Ensure option has either text or image_url (matching backend structure)
      return (option.text && option.text.trim()) || option.image_url
    })
  }

  // Handle legacy object format {A: "option1", B: "option2", ...} for backward compatibility
  if (typeof props.question.options === 'object' && !Array.isArray(props.question.options)) {
    return [
      { label: 'A', text: props.question.options.A, id: 'legacy_A' },
      { label: 'B', text: props.question.options.B, id: 'legacy_B' },
      { label: 'C', text: props.question.options.C, id: 'legacy_C' },
      { label: 'D', text: props.question.options.D, id: 'legacy_D' },
    ].filter((opt) => opt.text)
  }

  return []
})

// Validation rules for text answers
const textRules = [
  (v) => !!v || 'La respuesta es requerida',
  (v) => (v && v.length >= 3) || 'La respuesta debe tener al menos 3 caracteres',
]

// Check if answer can be submitted
const canSubmit = computed(() => {
  if (isMultipleChoice.value || isTrueFalse.value) {
    return selectedOption.value !== null && selectedOption.value !== undefined
  }

  if (isText.value || isEssay.value) {
    return textAnswer.value && textAnswer.value.trim().length >= 3
  }

  return false
})

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const submitAnswer = () => {
  if (!canSubmit.value || answered.value) return

  let answer

  if (isMultipleChoice.value || isTrueFalse.value) {
    answer = selectedOption.value
  } else if (isText.value || isEssay.value) {
    answer = textAnswer.value.trim()
  }

  answered.value = true
  emit('answer-selected', answer)
}

// Disable interaction when time runs out
watch(
  () => props.timeRemaining,
  (newTime) => {
    if (newTime <= 0 && !answered.value) {
      // Time's up - just disable the UI, store will handle submission
      answered.value = true
    }
  },
)

// Debug logging
onMounted(() => {
  console.log('EvaluationQuestion mounted with question:', props.question)
  console.log('Question options:', props.question?.options)
  console.log('Processed options:', questionOptions.value)
})

// Reset state when question changes
watch(
  () => props.question,
  (newQuestion) => {
    selectedOption.value = null
    textAnswer.value = ''
    answered.value = false

    // Debug new question
    console.log('Question changed:', newQuestion)
    console.log('New question options:', newQuestion?.options)
    console.log('Processed options:', questionOptions.value)
  },
  { immediate: true },
)
</script>

<style scoped>
.question-card {
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.question-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 1.5rem;
}

.question-image {
  display: flex;
  justify-content: center;
}

.options-container {
  padding: 1rem 0;
}

.options-group {
  gap: 0.5rem;
}

.option-card {
  border: 2px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  background: white;
}

.option-card:hover {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.option-card.option-selected {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.04);
  box-shadow: 0 2px 12px rgba(var(--v-theme-primary), 0.2);
}

.option-card.option-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.option-content {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  width: 100%;
  cursor: pointer;
  margin-bottom: 0;
}

.option-radio {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.option-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.option-image {
  margin-top: 0.5rem;
}

.option-image .v-img {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

/* Removed old radio-specific styles */

.option-letter {
  font-weight: 600;
  color: #1976d2;
  min-width: 24px;
  flex-shrink: 0;
}

.option-text {
  flex: 1;
  font-size: 1rem;
  line-height: 1.5;
}

.v-textarea {
  margin-top: 1rem;
}

/* Responsive design */
@media (max-width: 600px) {
  .question-card {
    margin: 0;
    border-radius: 0;
  }

  .option-content {
    padding: 0.5rem 0.75rem;
  }

  .v-card-text {
    padding: 1rem !important;
  }
}
</style>
